import { useEffect, useRef, useState } from 'react'
import { collection, doc, query, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { attendanceStatus } from '@shared/firestore/interests'
import { getDocs, updateDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { eventInterestsCol } from '../lib/converters'
import { createSetInterest, writeEventInterest } from '../lib/interestWrites'
import {
  getAllPicks,
  getPicksOwner,
  getTotalPickCount,
  removePickIfUnchanged,
  setPicksOwner,
  type LocalPicks,
} from '../lib/guestSetPicks'
import { useAuth } from './useAuth'
import { useOnlineStatus } from './useOnlineStatus'
import { track } from '../lib/analytics'
import { log } from '../lib/logger'
import type { SetInterest } from '../types'

// Moves a guest's device-only set hearts into the account they sign into.
// Only an account a watched guest → signed-in transition claimed them for: a
// session that starts signed in as anyone else ignores leftover picks, so a
// stranger's hearts on a shared device never land in someone else's account.
export function usePendingSetPicks() {
  const { signedInUid, loading } = useAuth()
  const online = useOnlineStatus()
  const visible = useDocumentVisible()
  const sawGuest = useRef(false)
  const inFlight = useRef(false)

  useEffect(() => {
    if (loading) return
    if (!signedInUid) {
      sawGuest.current = true
      return
    }
    if (sawGuest.current) {
      sawGuest.current = false
      if (getTotalPickCount() > 0) setPicksOwner(signedInUid)
    }
    if (getPicksOwner() !== signedInUid) return
    if (getTotalPickCount() === 0) {
      setPicksOwner(null)
      return
    }
    if (inFlight.current || !online || !visible) return
    inFlight.current = true
    void migrateAll(signedInUid)
      .then(() => {
        if (getTotalPickCount() === 0) setPicksOwner(null)
      })
      .finally(() => {
        inFlight.current = false
      })
  }, [signedInUid, loading, online, visible])
}

async function migrateAll(uid: string) {
  for (const { eventId, picks } of getAllPicks()) {
    try {
      await migrateEvent(uid, eventId, picks)
    } catch (err) {
      // Left on the device; retried on the next online / visibility change.
      log('guest-picks', 'migration failed', { eventId, error: String(err) })
    }
  }
}

async function migrateEvent(uid: string, eventId: string, picks: LocalPicks) {
  const setSnap = await getDocs(
    query(
      collection(db, 'setInterests'),
      where('userId', '==', uid),
      where('eventId', '==', eventId),
    ) as Query<SetInterest>,
  )
  const remote = new Map(setSnap.docs.map(d => [d.data().setId, { id: d.id, status: d.data().status }]))

  // Read first: createSetInterest merges, so blindly writing 'interested' would
  // demote a set the account already marked going. Never demote.
  await Promise.all(
    [...picks].map(([setId, status]) => {
      const existing = remote.get(setId)
      if (!existing) return createSetInterest(uid, eventId, setId, status)
      if (status === 'going' && existing.status !== 'going') {
        return updateDoc(doc(db, 'setInterests', existing.id), { status: 'going' })
      }
      return undefined
    }),
  )

  // After the sets, so seedSetsOnEventInterest's dedup sees them. Mirrors
  // ensureInterested / ensureGoing, without announcing an RSVP the user didn't tap.
  const anyGoing = [...picks.values()].includes('going')
  const eventSnap = await getDocs(
    query(eventInterestsCol(), where('userId', '==', uid), where('eventId', '==', eventId)),
  )
  const existing = eventSnap.docs[0]
  if (!existing) {
    await writeEventInterest(uid, eventId, null, anyGoing ? 'going' : 'interested')
  } else if (anyGoing && attendanceStatus(existing.data().status) !== 'going') {
    await writeEventInterest(uid, eventId, existing.id, 'going')
  }

  for (const [setId, status] of picks) removePickIfUnchanged(eventId, setId, status)
  track('guest_picks_migrated', { event_id: eventId, set_count: picks.size })
}

function useDocumentVisible() {
  const [visible, setVisible] = useState(() => document.visibilityState === 'visible')
  useEffect(() => {
    const onChange = () => setVisible(document.visibilityState === 'visible')
    document.addEventListener('visibilitychange', onChange)
    return () => document.removeEventListener('visibilitychange', onChange)
  }, [])
  return visible
}
